import { queryClient, queryKeys } from './query-client';
import { fetchWorkspaces } from './workspaces';
import { fetchWorkspaceFile, fetchWorkspaceFiles, type WorkspaceFile } from './workspace-files';
import { resolveVfsContent, workspaceFileRefOf } from './vfs-attachment';
import type { Message } from './use-chat';

// Helpers for the attachments rendered ON a message bubble. The Files PANEL's list lives in
// lib/conversation-attachments.ts.

type ChatAttachment = NonNullable<Message['attachments']>[number];

/** One chat attachment as the `WorkspaceFile` shape the `artifact-file.ts` open/share helpers and the
 *  doc viewer take — `content` holds the attachment's URL, which is the "content that is a bare URL"
 *  convention those helpers already infer from. */
export function attachmentFile(attachment: ChatAttachment): WorkspaceFile {
  return { name: attachment.fileName, isApp: false, content: attachment.url };
}

/** Resolve a `vfsPath` attachment (a workspace artifact attached BY PATH, `url: ''` by contract) to its
 *  content string, reusing the ALREADY-DEPLOYED workspace-artifacts routes: `/api/workspaces` (slug→id)
 *  then `/api/workspace-files` (the workspace's files, matched by name). We deliberately do NOT add a new
 *  read-by-path route — orion-web can't run a brand-new API route locally (next.config proxies to the
 *  deployed pod), so it would 404 in dev until it shipped. A `vfsPath` names the workspace by SLUG while
 *  those routes key by id, so map slug→id first. Name-matching goes through `resolveVfsContent`
 *  (`vfs-attachment.ts`), the one contract-tested resolver: the file whose `name` is the vfsPath minus its
 *  `workspaces/<slug>/` prefix (`artifacts/<rest>`), NOT the basename — a basename match returns null for a
 *  folder-qualified artifact and dropped the viewer to "isn't available to view here" (Monday 12756302538).
 *  The doc viewer renders the returned `content` exactly as a workspace file card's. Returns null when
 *  unresolved (viewer shows its honest "unavailable"). The native apps resolve the same artifact off the
 *  file-system doc they fetch — same file, per-surface source. */
export async function fetchVfsFileContent(vfsPath: string): Promise<string | null> {
  try {
    const ref = workspaceFileRefOf(vfsPath);
    if (!ref) return null;
    const workspaces = await queryClient.fetchQuery({ queryKey: queryKeys.workspaces, queryFn: fetchWorkspaces });
    const workspaceId = workspaces.find((w) => w.slug === ref.slug)?.id;
    if (!workspaceId) return null;
    const files = await queryClient.fetchQuery({
      queryKey: queryKeys.workspaceFiles(workspaceId),
      queryFn: () => fetchWorkspaceFiles(workspaceId),
    });
    return resolveVfsContent(vfsPath, files);
  } catch {
    return null;
  }
}

/** The content a markdown link to a workspace path opens — `vfsPath` in the attachment form (see
 *  `lib/vfs-link.ts`). The same two reads a `vfsPath` chip makes (`useAttachmentContent`), in the same
 *  order: the Space's already-loaded file list first, then the scoped single-file `/api/workspace-file`
 *  read, which is also the only one that reaches the account-level directories (`brain/`, `knowledge/`, …).
 *  Null when neither finds it, so the viewer opens to its honest "unavailable" state. Run at CLICK time
 *  rather than on render: a link, unlike a chip, is not announcing a file, and a message can carry many. */
export async function fetchVfsLinkContent(vfsPath: string): Promise<string | null> {
  const listed = await fetchVfsFileContent(vfsPath);
  if (listed) return listed;
  try {
    const content = await queryClient.fetchQuery({
      queryKey: queryKeys.workspaceFile(vfsPath),
      queryFn: () => fetchWorkspaceFile(vfsPath),
    });
    return content.trim() ? content : null;
  } catch {
    return null;
  }
}

